import { Check, GlobeHemisphereWest, MagnifyingGlass } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPref, setPref } from '@/core/settings';
import {
  ackDeviceZone,
  allZones,
  appZone,
  deviceZone,
  displayZoneAck,
  displayZoneOverride,
  formatOffset,
  formatTimeIn,
  isValidZone,
  setDisplayZone,
  zoneAbbr,
  zoneCity,
  zoneSwitchPrompt,
} from '@/core/time';
import { Button, cx, Input, useClockKey } from './components';
import { Sheet } from './overlays';
import './zone.css';

// Time zones: a small "CEST · Berlin" button for the display zone that opens a picker, the
// same picker for other choices (home zone, the zone a night was slept in), and a prompt when
// the device changes zone while a manual display zone is set. The display zone is per device
// (localStorage via core/time setDisplayZone), never synced.

const RECENT_KEY = 'time.recentZones';

/** "CEST · Berlin" (or "UTC"). */
export function zoneLabel(tz: string, ms = Date.now()): string {
  const abbr = zoneAbbr(tz, ms);
  const city = zoneCity(tz);
  return !abbr || abbr === city ? city : `${abbr} · ${city}`;
}

/** "Europe/Berlin · UTC+2" */
function zoneDetail(tz: string, ms: number): string {
  return `${tz} · ${formatOffset(tz, ms).replace('GMT', 'UTC')}`;
}

/** Zones worth listing first: the current pick, this device, the caller's hints, recent picks. */
export function suggestedZones(opts: {
  current: string | null;
  device: string;
  recent: string[];
  hints?: string[];
}): string[] {
  const out: string[] = [];
  for (const z of [opts.current, opts.device, ...(opts.hints ?? []), ...opts.recent]) {
    if (z && isValidZone(z) && !out.includes(z)) out.push(z);
    if (out.length >= 8) break;
  }
  return out;
}

export function ZoneIndicator({ compact = false }: { compact?: boolean }) {
  useClockKey(); // device zone, override and DST changes re-render this
  const [open, setOpen] = useState(false);
  const now = Date.now();
  const tz = appZone();
  const manual = displayZoneOverride() !== null;
  const device = deviceZone();
  const title = manual
    ? `${zoneDetail(tz, now)} · set for this device (it is on ${zoneCity(device)} time)`
    : `${zoneDetail(tz, now)} · following this device`;
  return (
    <>
      <button
        type="button"
        className={cx('zone-ind', manual && 'zone-ind--manual')}
        title={title}
        aria-label={`Time zone ${title}. Change time zone`}
        onClick={() => setOpen(true)}
      >
        <GlobeHemisphereWest size={14} />
        <span>{compact ? zoneAbbr(tz, now) || zoneCity(tz) : zoneLabel(tz, now)}</span>
      </button>
      {open && <ZonePicker onClose={() => setOpen(false)} />}
    </>
  );
}

export function ZonePicker({
  onClose,
  value,
  onPick,
  title = 'Time zone',
  intro = '“Today” and the times shown use this zone on this device only.',
  automatic = { label: 'Automatic — follow this device', sub: 'switches when you travel' },
  hints,
  at,
}: {
  onClose: () => void;
  /** the chosen zone (null = automatic); default: this device's display zone */
  value?: string | null;
  /** default: sets this device's display zone */
  onPick?: (tz: string | null) => void;
  title?: string;
  intro?: string;
  /** the "no fixed zone" row; false hides it */
  automatic?: { label: string; sub: string } | false;
  /** zones worth listing near the top (e.g. where Trace saw you that night) */
  hints?: string[];
  /** the moment whose clocks and offsets the list shows (default now) */
  at?: number;
}) {
  const [q, setQ] = useState('');
  // One "now" per open: the list shows each zone's clock at that moment.
  const [now] = useState(() => at ?? Date.now());
  const current = value === undefined ? displayZoneOverride() : value;
  const device = deviceZone();

  const suggested = useMemo(
    () => suggestedZones({ current, device, hints, recent: getPref<string[]>(RECENT_KEY, []) }),
    [current, device, hints],
  );
  const all = useMemo(() => allZones(), []);
  // Abbreviation, offset and clock per zone, worked out once per open (Intl is slowish).
  const info = useMemo(() => {
    const cache = new Map<string, { abbr: string; offset: string; time: string; text: string }>();
    return (z: string) => {
      let i = cache.get(z);
      if (!i) {
        const abbr = zoneAbbr(z, now);
        const offset = formatOffset(z, now);
        const text = `${z.replace(/_/g, ' ')} ${abbr} ${offset}`.toLowerCase();
        i = { abbr, offset, time: formatTimeIn(now, z), text };
        cache.set(z, i);
      }
      return i;
    };
  }, [now]);
  const query = q.trim().toLowerCase();
  const matches = useMemo(
    () => (query ? all.filter((z) => info(z).text.includes(query)).slice(0, 150) : []),
    [all, info, query],
  );

  const choose = (tz: string | null) => {
    if (onPick) onPick(tz);
    else setDisplayZone(tz);
    if (tz) {
      const recent = getPref<string[]>(RECENT_KEY, []).filter((z) => z !== tz);
      setPref(RECENT_KEY, [tz, ...recent].slice(0, 5));
    }
    onClose();
  };

  const row = (z: string) => (
    <button
      key={z}
      type="button"
      className={cx('zone-row', current === z && 'zone-row--sel')}
      onClick={() => choose(z)}
    >
      <span className="zone-row__check">{current === z && <Check size={16} weight="bold" />}</span>
      <span className="zone-row__main">
        <span className="zone-row__city">
          {zoneCity(z)}
          {z === device && <span className="zone-row__tag">this device</span>}
        </span>
        <span className="zone-row__sub">{z}</span>
      </span>
      <span className="zone-row__meta">
        <span>{info(z).time}</span>
        <span className="zone-row__sub">
          {info(z).abbr === info(z).offset || !info(z).abbr
            ? info(z).offset
            : `${info(z).abbr} · ${info(z).offset}`}
        </span>
      </span>
    </button>
  );

  return (
    <Sheet open onClose={onClose} title={title} fullHeight className="zone-sheet">
      <p className="small muted zone-sheet__intro">{intro}</p>
      {automatic && (
        <button
          type="button"
          className={cx('zone-row', current === null && 'zone-row--sel')}
          onClick={() => choose(null)}
        >
          <span className="zone-row__check">
            {current === null && <Check size={16} weight="bold" />}
          </span>
          <span className="zone-row__main">
            <span className="zone-row__city">{automatic.label}</span>
            <span className="zone-row__sub">{automatic.sub}</span>
          </span>
        </button>
      )}
      <div className="zone-sheet__search">
        <MagnifyingGlass size={16} />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search city, zone or offset (e.g. Tokyo, GMT+9)"
          aria-label="Search time zones"
          autoFocus={false}
        />
      </div>
      {query ? (
        <>
          <div className="zone-sheet__label">
            {matches.length ? 'Matches' : `No time zone matches “${q.trim()}”`}
          </div>
          {matches.map(row)}
        </>
      ) : (
        <>
          <div className="zone-sheet__label">Suggested</div>
          {suggested.map(row)}
          <div className="zone-sheet__label">All time zones</div>
          {all.filter((z) => !suggested.includes(z)).map(row)}
        </>
      )}
    </Sheet>
  );
}

/**
 * One-time question when the device changes zone while a manual zone is set (Automatic just
 * follows). Mounted once by the app shell.
 */
export function ZoneChangePrompt() {
  useClockKey();
  const p = zoneSwitchPrompt(displayZoneOverride(), deviceZone(), displayZoneAck());
  if (!p) return null;
  const now = Date.now();
  const to = zoneCity(p.to);
  const abbr = zoneAbbr(p.to, now);
  return createPortal(
    <div className="zone-prompt" role="status">
      <GlobeHemisphereWest size={18} className="zone-prompt__icon" />
      <div className="zone-prompt__text">
        Your device is now on {to} time{abbr ? ` (${abbr})` : ''}. Switch the app to {to}?
      </div>
      <div className="zone-prompt__actions">
        <Button size="sm" variant="primary" onClick={() => setDisplayZone(null)}>
          Switch
        </Button>
        <Button size="sm" variant="ghost" onClick={() => ackDeviceZone(p.to)}>
          Keep {zoneCity(p.from)}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
